import type { HostContract, SessionContext } from '@tsagko/label-contracts'
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { changeLocale, normalizeLocale } from '../i18n'
import { createDemoAdapter } from './demo'
import { createEmbeddedAdapter } from './embedded'
import { createTransport, type EmbeddedTransport } from './embedded/transport'
import { EmbedError } from './EmbedError'
import { seedStarters } from './seed-starters'

/** Feature flag an embedded session MUST advertise before the designer mounts. */
const REQUIRED_PROTOCOL_FEATURE = 'protocol:1'

/** Parses the build-time `VITE_ALLOWED_HOSTS` CSV into the exact-origin allowlist `createTransport` expects. */
function parseAllowedHosts(raw: string | undefined): string[] {
  return (raw ?? '')
    .split(',')
    .map(origin => origin.trim())
    .filter(origin => origin.length > 0)
}

type AdapterStatus =
  | { kind: 'loading' }
  | { kind: 'ready'; adapter: HostContract; sessionContext?: SessionContext }
  | { kind: 'error'; message: string }
  | { kind: 'protocol-error' }

export const AdapterContext = createContext<AdapterStatus>({ kind: 'loading' })

/** Which host environment the designer booted into. Demo mode persists to
 * IndexedDB/localStorage with no host to answer back to it; embedded mode is
 * iframed inside Odoo and talks to it over the postMessage transport. */
export type AdapterMode = 'demo' | 'embedded'

/** Same build-time check the bootstrap effect below reads
 * (`import.meta.env.VITE_ADAPTER === 'embedded'`) — mode is a pure function
 * of the build, not of the (async) bootstrap outcome, so it's available
 * synchronously on first render, before the adapter has finished loading. */
function detectAdapterMode(): AdapterMode {
  return import.meta.env.VITE_ADAPTER === 'embedded' ? 'embedded' : 'demo'
}

export const AdapterModeContext = createContext<AdapterMode>(detectAdapterMode())

async function bootstrapEmbedded(transport: EmbeddedTransport): Promise<AdapterStatus> {
  transport.start()
  const adapter = createEmbeddedAdapter({ transport })
  const sessionResult = await adapter.operations.getSessionContext()
  if (sessionResult.status !== 'ok') {
    // Thread the error code into the message so the AppShell error panel
    // lets operators tell UPSTREAM_UNAVAILABLE (host never answered) apart
    // from INTERNAL (host answered with a malformed session context).
    return {
      kind: 'error',
      message: `[${sessionResult.error.code}] ${sessionResult.error.message}`,
    }
  }
  const sessionContext = sessionResult.data
  if (!sessionContext.capabilities.features.includes(REQUIRED_PROTOCOL_FEATURE)) {
    return { kind: 'protocol-error' }
  }
  changeLocale(normalizeLocale(sessionContext.locale))
  return { kind: 'ready', adapter, sessionContext }
}

async function bootstrapDemo(): Promise<AdapterStatus> {
  const adapter = await createDemoAdapter()
  await seedStarters(adapter)
  const sessionResult = await adapter.operations.getSessionContext()
  const sessionContext = sessionResult.status === 'ok' ? sessionResult.data : undefined
  return { kind: 'ready', adapter, sessionContext }
}

export function AdapterProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AdapterStatus>({ kind: 'loading' })

  useEffect(() => {
    let cancelled = false
    // Created here (not inside the async helper) so the effect cleanup can
    // tear it down — otherwise a StrictMode double-invoke or any remount
    // would leak the discarded transport's window 'message' listener and
    // keep its handshake traffic flowing forever.
    let transport: EmbeddedTransport | undefined
    void (async () => {
      try {
        let next: AdapterStatus
        if (import.meta.env.VITE_ADAPTER === 'embedded') {
          transport = createTransport({
            allowedOrigins: parseAllowedHosts(import.meta.env.VITE_ALLOWED_HOSTS),
          })
          next = await bootstrapEmbedded(transport)
        } else {
          next = await bootstrapDemo()
        }
        if (!cancelled) setStatus(next)
      } catch (error) {
        if (!cancelled) {
          setStatus({
            kind: 'error',
            message: error instanceof Error ? error.message : 'unknown error',
          })
        }
      }
    })()
    return () => {
      cancelled = true
      // stop() is idempotent and settles any in-flight request locally, so
      // an unmount mid-handshake resolves the pending getSessionContext()
      // (whose result the cancelled flag then discards). Demo mode never
      // creates a transport — nothing to stop.
      transport?.stop()
    }
  }, [])

  if (status.kind === 'protocol-error') {
    return <EmbedError />
  }

  return (
    <AdapterModeContext.Provider value={detectAdapterMode()}>
      <AdapterContext.Provider value={status}>{children}</AdapterContext.Provider>
    </AdapterModeContext.Provider>
  )
}

export function useAdapter(): HostContract | undefined {
  const status = useContext(AdapterContext)
  return status.kind === 'ready' ? status.adapter : undefined
}

export function useAdapterStatus(): AdapterStatus {
  return useContext(AdapterContext)
}

export function useSessionContext(): SessionContext | undefined {
  const status = useContext(AdapterContext)
  return status.kind === 'ready' ? status.sessionContext : undefined
}

export function useAdapterMode(): AdapterMode {
  return useContext(AdapterModeContext)
}
